import { useRef, useEffect, useMemo, useState } from 'react'
import { fmtEur } from '@/shared/lib'
import { useIsMobile } from '@/shared/hooks'
import type { Winner } from '@/shared/types'

const FLAG_EMOJI: Record<string, string> = {
  DE: '🇩🇪', FR: '🇫🇷', GB: '🇬🇧', ES: '🇪🇸', IT: '🇮🇹', NL: '🇳🇱',
  AT: '🇦🇹', BE: '🇧🇪', PT: '🇵🇹', CH: '🇨🇭', IE: '🇮🇪', DK: '🇩🇰',
  SE: '🇸🇪', FI: '🇫🇮', NO: '🇳🇴', PL: '🇵🇱', CZ: '🇨🇿', LU: '🇱🇺',
  SK: '🇸🇰', HU: '🇭🇺',
}

function ReviewCard({ r, isNew }: { r: Winner; isNew?: boolean }) {
  return (
    <div
      className={`shrink-0 bg-surface-800 border rounded-xl p-2.5 sm:p-3 space-y-1.5 sm:space-y-2 transition-all duration-700 ${
        isNew ? 'border-amber-500/40 shadow-[0_0_12px_rgba(251,191,36,0.15)] animate-slide-down' : 'border-white/[0.06]'
      }`}
    >
      <p className="text-xs sm:text-sm text-gray-300 leading-snug line-clamp-2">{r.review_text}</p>
      <div className="flex items-center justify-between pt-1.5 sm:pt-2 border-t border-white/[0.04]">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <span className="text-sm sm:text-base">{FLAG_EMOJI[r.country_code] ?? '🌍'}</span>
          <p className="text-xs sm:text-sm font-bold text-white truncate">{r.short_name}</p>
          <span className="text-amber-400 text-[10px] sm:text-xs">★★★★★</span>
        </div>
        <span className="text-xs sm:text-sm font-bold text-emerald-400 shrink-0 ml-2">+{fmtEur(r.amount)}</span>
      </div>
    </div>
  )
}

const SPEED = 0.4

export function ReviewsBlock({ winners }: { winners: Winner[] }) {
  const isMobile = useIsMobile()
  const reviews = useMemo(() => winners.filter(w => w.review_text).slice(0, 20), [winners])
  const mobileReviews = useMemo(() => reviews.slice(-3), [reviews])
  const scrollRef = useRef<HTMLDivElement>(null)
  const [freshKey, setFreshKey] = useState<string | null>(null)
  const animPauseRef = useRef(false)

  // Periodically highlight the first review as "just appeared"
  useEffect(() => {
    if (reviews.length === 0 || isMobile) return
    const scheduleNext = () => setTimeout(() => {
      const el = scrollRef.current
      if (el) el.scrollTop = 0
      animPauseRef.current = true
      setFreshKey(`new-${Date.now()}`)
      setTimeout(() => {
        setFreshKey(null)
        animPauseRef.current = false
      }, 3000)
      timerId = scheduleNext()
    }, 35000 + Math.random() * 10000)
    let timerId = scheduleNext()
    return () => clearTimeout(timerId)
  }, [reviews, isMobile])

  useEffect(() => {
    const el = scrollRef.current
    if (!el || reviews.length === 0 || isMobile) return

    let raf: number
    let paused = false

    const step = () => {
      if (!paused && !animPauseRef.current && el) {
        el.scrollTop += SPEED
        if (el.scrollTop >= el.scrollHeight / 2) {
          el.scrollTop -= el.scrollHeight / 2
        }
      }
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)

    const pause = () => { paused = true }
    const resume = () => { paused = false }
    el.addEventListener('mouseenter', pause)
    el.addEventListener('mouseleave', resume)
    el.addEventListener('touchstart', pause)
    el.addEventListener('touchend', resume)

    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('mouseenter', pause)
      el.removeEventListener('mouseleave', resume)
      el.removeEventListener('touchstart', pause)
      el.removeEventListener('touchend', resume)
    }
  }, [reviews.length, isMobile])

  if (reviews.length === 0) return null

  const displayReviews = isMobile ? mobileReviews : [...reviews, ...reviews]

  return (
    <div className="card overflow-hidden h-full min-h-[260px] flex flex-col">
      <div className="px-3 sm:px-4 py-2 sm:py-2.5 border-b border-white/[0.06] flex items-center justify-between shrink-0">
        <h2 className="text-sm sm:text-base font-extrabold text-white">What Traders Say</h2>
        <div className="flex items-center gap-1.5">
          <span className="text-amber-400 text-sm">★★★★★</span>
          <span className="text-xs font-bold text-gray-400">4.9</span>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="overflow-hidden px-2 sm:px-3 py-2 flex-1 min-h-0"
        style={isMobile ? undefined : {
          maskImage: 'linear-gradient(to bottom, transparent, black 6%, black 94%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 6%, black 94%, transparent)',
        }}
      >
        <div className="flex flex-col gap-2">
          {displayReviews.map((r, i) => (
            <ReviewCard key={freshKey && i === 0 ? freshKey : `${r.id}-${i}`} r={r} isNew={!isMobile && !!freshKey && i === 0} />
          ))}
        </div>
      </div>
    </div>
  )
}
